import Button from '@pelckmans/elementis/components/button';
import SelectField from '@pelckmans/elementis/components/select-field';
import SelectList, { type Item } from '@pelckmans/elementis/components/select-list';
import { useEffect, useState, type ReactNode, type SetStateAction } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { fetchCurrentGroups, fetchMembers, type CurrentGroup, type Member } from '../../../api/groups';
import { getBrowserInfo } from '../../../utils/browserInfo';
import { fetchLicensedModules, type LicensedModule } from '../api/licensedModules';
import { postRequestLicenseCodes, type LicenseCodeRequest } from '../api/support';

const BASE = 'PAGES.HELP.SECTIONS.CONTACT';

export type RequestCodeFormError = 'noTeacherLicense' | 'invalid' | 'unhandled' | 'maxMembersExceeded';

interface withName {
  id: string;
  name?: string;
  niceName?: string;
}

interface FormValues {
  superModule: LicensedModule;
  module: LicensedModule;
  group: CurrentGroup;
  memberItems: Item[];
}

interface ControlledSelectFieldProps {
  name: keyof FormValues;
  labelText: string;
  ariaLabel: string;
  placeholder?: string;
  disabledPlaceholder?: string;
  children: ReactNode;
  renderField?: keyof withName;
}

interface RequestCodeFormProps {
  onError: (error: RequestCodeFormError) => void;
  onSuccess: () => void;
  studioGroup: StudioGroup;
}

export default function ContactLicenseForm({ onError, onSuccess, studioGroup }: RequestCodeFormProps) {
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid, isSubmitted },
  } = useForm<FormValues>({
    mode: 'onBlur',
  });

  const [isLoading, setIsLoading] = useState(true);
  const [licensedModules, setLicensedModules] = useState<LicensedModule[]>([]);
  const [groups, setGroups] = useState<CurrentGroup[]>([]);

  const [modules, setModules] = useState<LicensedModule[]>([]);
  const [memberItems, setMemberItems] = useState<Item[]>([]);

  const superModuleValue = watch('superModule');
  const currentGroupValue = watch('group');

  useEffect(() => {
    if (superModuleValue) {
      const modules = [superModuleValue, ...(superModuleValue.subModules || [])].sort((a, b) =>
        a.niceName.localeCompare(b.niceName, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      );

      setModules(modules);
    } else {
      setModules([]);
    }
  }, [superModuleValue]);

  useEffect(() => {
    async function getMembers() {
      try {
        const data = await fetchMembers(currentGroupValue.schoolYear, currentGroupValue.id);

        const memberItems: Item[] = data
          .filter((x: Member) => x.status === 'accepted')
          .map(member => ({
            id: member.id,
            value: `${member.firstName} ${member.lastName}`,
            checked: false,
          }));

        setMemberItems(memberItems);
      } catch (error) {
        onError('unhandled');
      }
    }

    if (currentGroupValue) {
      getMembers();
    }
  }, [currentGroupValue, onError]);

  useEffect(() => {
    async function initialise() {
      try {
        setIsLoading(true);

        const [licenseModuleData, currentGroupData] = await Promise.all([fetchLicensedModules(), fetchCurrentGroups()]);

        setIsLoading(false);
        if (licenseModuleData.length === 0) onError('noTeacherLicense');

        setLicensedModules(licenseModuleData);
        setGroups(currentGroupData);
      } catch (error) {
        setIsLoading(false);
        onError('unhandled');
      }
    }

    initialise();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSubmitted && !isValid) onError('invalid');
  }, [isSubmitted, isValid, onError]);

  const onSubmit = async (data: FormValues) => {
    const members = data.memberItems.filter(member => member.checked).map(member => member.id);

    if (members.length > 50) onError('invalid');

    const licenseCodeRequest: LicenseCodeRequest = {
      superModuleId: data.superModule.id,
      moduleId: data.module.id,
      groupId: data.group.id,
      members,
      technicalInfo: getBrowserInfo(),
    };

    try {
      await postRequestLicenseCodes(studioGroup, licenseCodeRequest);

      onSuccess();
    } catch (error) {
      onError('unhandled');
      console.error('Error sending user message:', error);
    }
  };

  const ControlledSelectField = ({
    name,
    labelText,
    ariaLabel,
    placeholder,
    disabledPlaceholder,
    children,
    renderField = 'name',
  }: ControlledSelectFieldProps) => {
    return (
      <Controller
        name={name}
        control={control}
        rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
        render={({ field, fieldState }) => (
          <SelectField
            labelText={labelText}
            required
            // @ts-ignore
            value={field.value}
            onChange={value => field.onChange(value)}
            renderValueContent={value => {
              if (value && typeof value === 'object' && renderField in value) {
                return (value as withName)[renderField] ?? '';
              }
              return '';
            }}
            dropdownMenuProps={{ ariaLabel }}
            error={!!fieldState.error}
            errorText={fieldState.error?.message}
            {...(placeholder && { placeholder })}
            {...(disabledPlaceholder ? { disabled: true, placeholder: disabledPlaceholder } : {})}
          >
            {children}
          </SelectField>
        )}
      />
    );
  };

  if (isLoading) return null;

  return (
    <form id="request-code-form" data-testid="request-code-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="contact-form__fields">
        <ControlledSelectField
          name="superModule"
          labelText={t(`${BASE}.SUPER_MODULE.LABEL`)}
          ariaLabel={t(`${BASE}.SUPER_MODULE.A11Y_LABEL`)}
          placeholder={t(`${BASE}.SUPER_MODULE.PLACEHOLDER`)}
        >
          {licensedModules.map(licensedModule => (
            <SelectField.Item key={licensedModule.id} value={licensedModule} text={licensedModule.name} />
          ))}
        </ControlledSelectField>
        <ControlledSelectField
          name="module"
          labelText={t(`${BASE}.MODULE.LABEL`)}
          ariaLabel={t(`${BASE}.MODULE.A11Y_LABEL`)}
          placeholder={t(`${BASE}.MODULE.PLACEHOLDER`)}
          {...(modules.length === 0 && { disabledPlaceholder: t(`${BASE}.MODULE.DISABLED_PLACEHOLDER`) })}
          renderField="niceName"
        >
          {modules.map(module => (
            <SelectField.Item key={module.id} value={module} text={module.niceName} />
          ))}
        </ControlledSelectField>
        <ControlledSelectField
          name="group"
          labelText={t(`${BASE}.GROUP.LABEL`)}
          ariaLabel={t(`${BASE}.GROUP.A11Y_LABEL`)}
          placeholder={t(`${BASE}.GROUP.PLACEHOLDER`)}
        >
          {groups.map(group => (
            <SelectField.Item key={group.id} value={group} text={group.name} />
          ))}
        </ControlledSelectField>
        <Controller
          name={'memberItems'}
          control={control}
          rules={{
            required: t('FORM.VALIDATION.REQUIRED'),
            validate: {
              atLeastOneChecked: (items: Item[]) =>
                Array.isArray(items) && items.some(item => item.checked) ? true : t('FORM.VALIDATION.REQUIRED'),
            },
          }}
          render={({ field, fieldState }) => (
            <SelectList
              actions={{
                'aria-label': t(`${BASE}.MEMBERS.ACTIONS.A11Y_LABEL`),
                include: true,
                translations: {
                  checkAllText: t('FORM.SELECT_LIST.ACTIONS.CHECK_ALL'),
                  unCheckAllText: t('FORM.SELECT_LIST.ACTIONS.UNCHECK_ALL'),
                },
              }}
              // https://pelckmans.atlassian.net/browse/PAKP-9897
              ariaLabels={{
                ariaLabelCheckList: t(`${BASE}.MEMBERS.CHECKLIST.A11Y_LABEL`),
                ariaLabelLoading: t(`${BASE}.MEMBERS.LOADING.A11Y_LABEL`),
                ariaLabelList: t(`${BASE}.MEMBERS.LIST.A11Y_LABEL`),
                ariaLabelTextField: '',
              }}
              columns={2}
              labelText={t(`${BASE}.MEMBERS.LABEL`)}
              empty={memberItems.length === 0 ? { text: t(`${BASE}.MEMBERS.DISABLED_PLACEHOLDER`) } : undefined}
              items={memberItems}
              setItems={(setItems: SetStateAction<Item[]>) => {
                // Handle callback and sync state
                const items = typeof setItems === 'function' ? setItems(memberItems) : setItems;

                setMemberItems(items);
                field.onChange(items);
              }}
              isLoading={isLoading}
              error={!!fieldState.error}
              errorText={fieldState.error?.message}
            >
              {memberItems.map(item => (
                <SelectList.Item
                  id={item.id}
                  key={item.id}
                  onChange={(id: string, checked: boolean) => {
                    const items = memberItems.map(member => (member.id === id ? { ...member, checked } : member));

                    setMemberItems(items);
                    field.onChange(items);
                  }}
                  checked={item.checked}
                  searchString={item.value}
                >
                  {item.value}
                </SelectList.Item>
              ))}
            </SelectList>
          )}
        />
      </div>
      <div className="contact-form__submit">
        <Button prominence="success" type="submit">
          {t(`${BASE}.ACTIONS.SUBMIT`)}
        </Button>
      </div>
    </form>
  );
}
